import { type CSSProperties } from 'react';
import { type ProductCategory, type Rarity } from '@kon/shared';
import { imageUrl } from '@/lib/env';
import styles from './ProductImage.module.css';

const CATEGORY_GLYPH: Record<ProductCategory, string> = {
  seed: '🌱',
  pet: '🐾',
  gear: '🛠️',
  currency: '🪙',
  bundle: '🎁',
};

interface ProductImageProps {
  imageKey: string | null;
  name: string;
  category: ProductCategory;
  rarity: Rarity;
  /** Native lazy-loading hint; eager for above-the-fold hero images. */
  loading?: 'lazy' | 'eager';
}

/**
 * Product imagery with a themed fallback. When no R2 image is set, renders a
 * rarity-tinted gradient panel with the category glyph so the grid still looks
 * intentional and on-brand.
 */
export function ProductImage({ imageKey, name, category, rarity, loading = 'lazy' }: ProductImageProps) {
  const url = imageUrl(imageKey);

  if (url) {
    return (
      <div className={styles.wrap}>
        <img className={styles.img} src={url} alt={name} loading={loading} decoding="async" />
      </div>
    );
  }

  const placeholderStyle: CSSProperties = {
    background: `linear-gradient(150deg, var(--rarity-${rarity}-soft), var(--color-surface) 70%)`,
  };
  return (
    <div className={styles.wrap}>
      <div className={styles.placeholder} style={placeholderStyle} role="img" aria-label={name}>
        <span className={styles.sheen} aria-hidden="true" />
        <span className={styles.glyph} aria-hidden="true">
          {CATEGORY_GLYPH[category]}
        </span>
      </div>
    </div>
  );
}
